import React from 'react'
import { Box, Button, ButtonBase, Typography } from '@mui/material'

import { AskIrisButton } from '@components'
import { dsColorNew } from '@palette'
import { pct, points, tone, marketTone } from '@utils/colourScale'
import { cellInk } from './DistanceCell'
import { rankingEvidence } from '../../../@services/askEvidence'


const card = { flex: '1 1 260px', minWidth: 240, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '12px', p: 1.5, backgroundColor: dsColorNew.white }

function Entry({ entry, value, t, onOpen }) {
	return <ButtonBase onClick={() => onOpen(entry.officeId, entry.column)} sx={{ width: '100%', justifyContent: 'space-between', py: 0.5, px: 0.75, borderRadius: '6px', '&:hover': { backgroundColor: dsColorNew.hoverBgLight } }}>
		<Box component="span" sx={{ fontSize: 13, color: dsColorNew.textDark, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.officeName} · {entry.columnLabel}</Box>
		<Box component="span" sx={{ fontSize: 13, fontWeight: 700, color: cellInk(t), whiteSpace: 'nowrap', ml: 1 }}>{value}</Box>
	</ButtonBase>
}

// "Ask Iris" (CU-869fkdhjv phase 3, UX C10): an icon in the corner of a card with entries; `ask` =
// `{ askable, scope, since, sinceText, scopeText }`, `kind` = above / below / change / market
function Card({ title, subtitle, entries, empty, render, onOpen, kind, ask = null }) {
	const titleText = <Typography sx={{ fontSize: 13, fontWeight: 700, color: dsColorNew.textTitle, flex: 1, minWidth: 0 }}>{title}</Typography>
	return <Box sx={card}>
		{ask?.askable && entries.length
			? <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
				{titleText}
				<AskIrisButton variant="icon" tooltip={`Ask Iris about ${title}`} sx={{ my: -0.5, mr: -0.5 }}
					getAsk={() => rankingEvidence({ card: kind, title, subtitle, entries, ...ask })}/>
			</Box>
			: titleText}
		{!!subtitle && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mb: 0.75 }}>{subtitle}</Typography>}
		{entries.length ? entries.map(e => <Entry key={`${e.officeId}-${e.column}`} entry={e} onOpen={onOpen} {...render(e)}/>)
			: <Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, py: 0.75 }}>{empty}</Typography>}
	</Box>
}

/**
 * Rankings (CA 17), open by default (decision 6), foldable to one line; without identity the card "Where the market
 * moved most" in their place (P6, CA 21). Every entry opens its cell.
 */
export default function Rankings({ rankings, moves, open, onToggle, since, noComparison, total, scopeText, onOpen, ask = null }) {
	const cardAsk = ask ? { ...ask, sinceText: since, scopeText } : null
	const distance = e => {
		const t = tone(e.value)
		return { value: `${t.arrow} ${pct(e.value)}`, t }
	}
	const change = e => ({ value: `${e.value < 0 ? '▼' : '▲'} ${points(e.value)}`, t: { side: e.value < 0 ? 'cheaper' : 'dearer' } })
	const market = e => {
		const t = marketTone(e.value)
		return { value: `${t.arrow} ${pct(e.value)}`, t }
	}

	const header = <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: open ? 1 : 0, flexWrap: 'wrap' }}>
		<Typography sx={{ fontSize: 13, fontWeight: 700, color: dsColorNew.textTitle }}>{moves ? 'Where the market moved most' : 'Rankings'}</Typography>
		<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>· top 5 of {total} cells · {scopeText}</Typography>
		{!open && rankings && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
			{rankings.above[0] && <> · most above: {rankings.above[0].officeName} · {rankings.above[0].columnLabel} <b style={{ color: cellInk({ side: 'dearer' }) }}>▲ {pct(rankings.above[0].value)}</b></>}
			{rankings.below[0] && <> · most below: {rankings.below[0].officeName} · {rankings.below[0].columnLabel} <b style={{ color: cellInk({ side: 'cheaper' }) }}>▼ {pct(rankings.below[0].value)}</b></>}
		</Typography>}
		<Box sx={{ flex: 1 }}/>
		<Button size="small" variant="text" onClick={onToggle} sx={{ textTransform: 'none' }}>{open ? 'Hide rankings' : 'Show rankings'}</Button>
	</Box>

	return <Box component="section" aria-label="Rankings">
		{header}
		{open && <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
			{rankings && <>
				<Card kind="above" ask={cardAsk} title="Most above the market" entries={rankings.above} render={distance} empty="Nowhere above the market by 2% or more." onOpen={onOpen}/>
				<Card kind="below" ask={cardAsk} title="Most below the market" entries={rankings.below} render={distance} empty="Nowhere below the market by 2% or more." onOpen={onOpen}/>
				<Card kind="change" ask={cardAsk} title="Biggest change" subtitle={`since ${since} · points`} entries={rankings.change} render={change}
					empty={noComparison || 'Your distance did not move by half a point anywhere.'} onOpen={onOpen}/>
			</>}
			{moves && <Card kind="market" ask={cardAsk} title="Where the market moved most" subtitle={`since ${since} · change of the market median`} entries={moves} render={market}
				empty={noComparison || 'The market median did not move by 1% anywhere.'} onOpen={onOpen}/>}
		</Box>}
	</Box>
}
